import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import { CreateButton, TopToolbar } from 'react-admin';
import AddIcon from '@mui/icons-material/Add';

// GraphQL query for sites
const listSites = /* GraphQL */ `
  query ListSites {
    listSites {
      items {
        id
        name
        address
        customer_name
      }
    }
  }
`;

const ReportsActions = ({ title }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSites, setLoadingSites] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    siteId: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 3 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      setLoadingSites(true);
      const response = await API.graphql(graphqlOperation(listSites));
      setSites(response.data.listSites.items);
    } catch (error) {
      // console.error('Error fetching sites:', error);
      setSites([]);
    } finally {
      setLoadingSites(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setFormData({
      siteId: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
    });
    setError('');
  };

  const handleCloseModal = () => {
    if (!loading) {
      setIsModalOpen(false);
      setError('');
    }
  };

  const handleGenerate = async () => {
    if (!formData.siteId) {
      setError('Please select a site');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await API.post('record', '/monthly-reports', {
        body: {
          month: formData.month.toString(),
          year: formData.year.toString(),
          siteId: formData.siteId,
        },
      });

      if (response.reportUrl) {
        const link = document.createElement('a');
        link.href = response.reportUrl;
        link.download = `monthly-report-${formData.year}-${formData.month
          .toString()
          .padStart(2, '0')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        handleCloseModal();
      } else {
        throw new Error('No report URL received');
      }
    } catch (err) {
      // console.error('Error generating report:', err);
      setError(
        err.response?.data?.error || err.message || 'Failed to generate report'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TopToolbar
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography
          variant='h4'
          component='h1'
        >
          {title || 'Reports'}
        </Typography>

        <CreateButton
          label='Generate Report'
          icon={<AddIcon />}
          onClick={handleOpenModal}
          disabled={loadingSites}
          variant='contained'
          sx={{
            textTransform: 'none',
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            padding: '8px 14px'
          }}
        />
      </TopToolbar>

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Generate Monthly Report</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              name='siteId'
              label='Select Site'
              value={formData.siteId}
              onChange={handleInputChange}
              disabled={loading || loadingSites}
              required
              fullWidth
            >
              <MenuItem value=''>Choose a site...</MenuItem>
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                name='month'
                label='Month'
                value={formData.month}
                onChange={handleInputChange}
                disabled={loading}
                fullWidth
              >
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                name='year'
                label='Year'
                value={formData.year}
                onChange={handleInputChange}
                disabled={loading}
                fullWidth
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            variant='contained'
            disabled={loading || !formData.siteId}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportsActions;
export { ReportsActions };